import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import ReactMarkdown from 'react-markdown'
import type {
  ZoningQACitation,
  ZoningQALanguage,
  ZoneQAFeedbackType,
  ZoneDocumentsGroupedResponse,
} from '@popety-io/types'
import { Icon } from '../../atoms'
import { useBreakpoint } from '../../../hooks/useBreakpoint'
import { PDFViewer } from '../../organisms/PDFViewer'
import {
  buildDocumentFilters,
  type ZoneQAContext,
  type ZoneQAService,
  type ZoneQATranslations,
} from '../../organisms/ZoningSection/components/ZoneQA/types'
import { useZoneQA } from '../../organisms/ZoningSection/components/ZoneQA/useZoneQA'
import { ZoneQAFeedback } from './components/ZoneQAFeedback'
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  BackToChatButton,
  ChatPanel,
  MessagesContainer,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  MessageWrapper,
  MessageBubble,
  CitationsContainer,
  CitationChip,
  CitationIcon,
  InputArea,
  TextInput,
  // MicButton,
  SendButton,
  ViewSourceFab,
  LoadingIndicator,
  LoadingSpinner,
  ErrorMessage,
  PDFViewerPanel,
  PDFEmptyState,
  PDFEmptyStateIcon,
  PDFEmptyStateText,
} from './ZoneQAModal.styles'

const DEFAULT_TRANSLATIONS: ZoneQATranslations = {
  title: 'Ask about this zone',
  inLocation: 'in',
  placeholder: 'Ask a question about this zone...',
  send: 'Send',
  loading: 'Thinking...',
  emptyState: 'Ask any question about zoning regulations for this zone.',
  suggestDocs: 'View zone documents',
  errorRetry: 'An error occurred. Please try again.',
  citationLabels: {
    communal: 'Municipal',
    cantonal: 'Cantonal',
  },
  feedback: {
    helpful: 'Helpful',
    notHelpful: 'Not helpful',
    thankYou: 'Thank you for your feedback!',
    feedbackError: 'Failed to submit feedback',
  },
}

export interface ZoneQAModalProps {
  isOpen: boolean
  onClose: () => void
  zoneContext: ZoneQAContext
  zoningService: ZoneQAService
  language: ZoningQALanguage
  translations?: ZoneQATranslations
  /** Zone documents for filtering chunks - if provided, enables document-based filtering */
  zoneDocuments?: ZoneDocumentsGroupedResponse | null
  /** Callback to transcribe voice audio. Receives an audio Blob, returns transcribed text. */
  onTranscribeVoice?: (audioBlob: Blob) => Promise<string>
}

interface ActivePDF {
  url: string
  page: number
  citationKey: string
}

export const ZoneQAModal: React.FC<ZoneQAModalProps> = ({
  isOpen,
  onClose,
  zoneContext,
  zoningService,
  language,
  translations,
  zoneDocuments,
  onTranscribeVoice,
}) => {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }
  const [inputValue, setInputValue] = useState('')
  const [activePDF, setActivePDF] = useState<ActivePDF | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  // Mobile tab switching: 'chat' shows the conversation, 'pdf' shows the source
  // viewer. Ignored on desktop (both panels render side-by-side there).
  const [activeView, setActiveView] = useState<'chat' | 'pdf'>('chat')
  const { isDesktop } = useBreakpoint()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  // Tracks the assistant message id we've already auto-opened the first
  // citation for, so user clicks on other citations aren't reverted.
  const autoSelectedMessageIdRef = useRef<string | null>(null)

  // Build document filters from zone documents
  const documentFilters = useMemo(
    () => buildDocumentFilters(zoneDocuments),
    [zoneDocuments]
  )

  const {
    messages,
    isStreaming,
    error,
    sendMessage,
    clearError,
    submitFeedback,
  } = useZoneQA({
    zoneContext,
    zoningService,
    language,
    documentFilters,
  })

  // Track which messages have had feedback submitted
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<Set<string>>(
    new Set()
  )

  // Handler for feedback submission
  const handleFeedbackSubmit = useCallback(
    async (
      messageId: string,
      rating: number,
      feedbackType?: ZoneQAFeedbackType,
      comment?: string
    ) => {
      await submitFeedback(messageId, rating, feedbackType, comment)
      setFeedbackSubmitted((prev) => new Set(prev).add(messageId))
    },
    [submitFeedback]
  )

  // Check if feedback is available (service has submitFeedback method)
  const feedbackEnabled = Boolean(zoningService.submitFeedback)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Escape-to-close. Critical on mobile where the modal is full-viewport and
  // there's no backdrop area left to tap outside of.
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  // Auto-select first citation once per new assistant message. Tracking by
  // message id (not by comparing against activePDF) prevents a feedback loop
  // where clicking a different citation gets reverted back to the first one.
  useEffect(() => {
    if (messages.length === 0) return

    const lastMessage = messages[messages.length - 1]
    if (
      lastMessage.role === 'assistant' &&
      lastMessage.citations &&
      lastMessage.citations.length > 0 &&
      !isStreaming &&
      autoSelectedMessageIdRef.current !== lastMessage.id
    ) {
      autoSelectedMessageIdRef.current = lastMessage.id
      const firstCitation = lastMessage.citations[0]
      setActivePDF({
        url: firstCitation.document_url,
        page: firstCitation.page_start - 1, // 0-indexed for PDFViewer
        citationKey: `${firstCitation.document_url}-0`,
      })
    }
  }, [messages, isStreaming])

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim() || isStreaming) return

    const message = inputValue
    setInputValue('')
    await sendMessage(message)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.stop()
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [])

  const _handleMicClick = useCallback(async () => {
    if (!onTranscribeVoice) return

    if (isRecording) {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.stop()
      }
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
          streamRef.current = null
        }
        setIsRecording(false)

        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        })

        try {
          setIsTranscribing(true)
          const text = await onTranscribeVoice(audioBlob)
          if (text) {
            setInputValue((prev) => (prev ? `${prev} ${text}` : text))
            inputRef.current?.focus()
          }
        } catch {
          // Silently fail
        } finally {
          setIsTranscribing(false)
        }
      }

      mediaRecorder.start()
      setIsRecording(true)
    } catch {
      setIsRecording(false)
    }
  }, [isRecording, onTranscribeVoice])

  const handleCitationClick = useCallback(
    (citation: ZoningQACitation, idx: number, messageId: string) => {
      // Citation key includes the message id so the same citation index in
      // different messages doesn't share active state — clicking [1] in
      // message A must not highlight [1] in message B.
      const citationKey = `${messageId}::${idx}`
      setActivePDF({
        url: citation.document_url,
        page: citation.page_start - 1, // 0-indexed for PDFViewer
        citationKey,
      })
      // On mobile the panels are mutually exclusive, so tapping a citation
      // needs to swap the visible panel too. Safe no-op on desktop thanks to
      // the display: flex/none media query gate.
      setActiveView('pdf')
    },
    []
  )

  const formatPageRange = (pageStart: number, pageEnd?: number): string => {
    if (!pageEnd || pageEnd === pageStart) {
      return `p. ${pageStart}`
    }
    return `p. ${pageStart}-${pageEnd}`
  }

  const getCitationLabel = (granularity: string): string => {
    if (granularity === 'region') {
      return t.citationLabels?.cantonal || 'Cantonal'
    }
    return t.citationLabels?.communal || 'Municipal'
  }

  const isCitationActive = (idx: number, messageId: string): boolean => {
    if (!activePDF) return false
    return activePDF.citationKey === `${messageId}::${idx}`
  }

  if (!isOpen) return null

  // Render into document.body so the fixed-positioned overlay escapes any
  // ancestor containing blocks (overflow: hidden, transform, etc.) that would
  // otherwise clip it or keep it from covering the app header.
  return createPortal(
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          {activeView === 'pdf' && (
            <BackToChatButton
              type="button"
              onClick={() => setActiveView('chat')}
              aria-label={t.title}
            >
              <Icon name="chevron-left" size="medium" />
            </BackToChatButton>
          )}
          <ModalTitle>
            <Icon name="message-circle" size="medium" />
            <span>
              {t.title}: {zoneContext.zoneName} {t.inLocation || 'in'}{' '}
              {zoneContext.municipalityName}
            </span>
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <Icon name="x" size="medium" />
          </CloseButton>
        </ModalHeader>

        {/* Chat Panel */}
        <ChatPanel $activeView={activeView}>
          <MessagesContainer>
            {messages.length === 0 && !isStreaming ? (
              <EmptyState>
                <EmptyStateIcon>
                  <Icon name="help-circle" size="large" />
                </EmptyStateIcon>
                <EmptyStateText>{t.emptyState}</EmptyStateText>
              </EmptyState>
            ) : (
              <>
                {messages.map((message) => (
                  <MessageWrapper key={message.id} $role={message.role}>
                    <MessageBubble $role={message.role}>
                      {message.role === 'user' ? (
                        message.content
                      ) : (
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      )}
                    </MessageBubble>
                    {/* Citations */}
                    {message.role === 'assistant' &&
                      message.citations &&
                      message.citations.length > 0 && (
                        <CitationsContainer>
                          {message.citations.map((citation, idx) => {
                            const pageRange = formatPageRange(
                              citation.page_start,
                              citation.page_end
                            )
                            return (
                              <CitationChip
                                key={`${message.id}::${idx}`}
                                $granularity={citation.granularity}
                                $isActive={isCitationActive(idx, message.id)}
                                onClick={() =>
                                  handleCitationClick(citation, idx, message.id)
                                }
                                type="button"
                                title={`${citation.document_name} - ${pageRange}`}
                              >
                                <CitationIcon>
                                  <Icon name="file-text" size="small" />
                                </CitationIcon>
                                <span>[{citation.citation_index}]</span>
                                <span>
                                  {getCitationLabel(citation.granularity)}
                                </span>
                                <span>{pageRange}</span>
                              </CitationChip>
                            )
                          })}
                        </CitationsContainer>
                      )}
                    {/* Feedback buttons for assistant messages */}
                    {message.role === 'assistant' &&
                      feedbackEnabled &&
                      !isStreaming &&
                      !message.id.startsWith('assistant_') && (
                        <ZoneQAFeedback
                          messageId={message.id}
                          onSubmit={handleFeedbackSubmit}
                          disabled={feedbackSubmitted.has(message.id)}
                          translations={t.feedback}
                        />
                      )}
                  </MessageWrapper>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}

            {/* Loading indicator */}
            {isStreaming && messages.length > 0 && (
              <LoadingIndicator>
                <LoadingSpinner />
                <span>{t.loading}</span>
              </LoadingIndicator>
            )}
          </MessagesContainer>

          {/* Error message */}
          {error && (
            <ErrorMessage onClick={clearError}>
              <Icon name="alert-circle" size="small" />
              <span>{error}</span>
            </ErrorMessage>
          )}

          {/* Input */}
          <InputArea onSubmit={handleSubmit}>
            <TextInput
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t.placeholder}
              disabled={isStreaming || isTranscribing}
              rows={1}
            />
            {/* {onTranscribeVoice && (
              <MicButton
                type="button"
                $recording={isRecording}
                onClick={handleMicClick}
                disabled={isStreaming || isTranscribing}
              >
                <Icon name={isRecording ? 'stop' : 'microphone'} size="small" />
              </MicButton>
            )} */}
            <SendButton
              type="submit"
              $disabled={!inputValue.trim() || isStreaming || isTranscribing}
              disabled={!inputValue.trim() || isStreaming || isTranscribing}
            >
              <Icon name="send" size="small" />
            </SendButton>
          </InputArea>

          {/* Mobile-only shortcut back into the PDF viewer once a source is loaded */}
          {activePDF && !isDesktop && activeView === 'chat' && (
            <ViewSourceFab type="button" onClick={() => setActiveView('pdf')}>
              <Icon name="file-text" size="small" />
              <span>{t.suggestDocs || 'View source'}</span>
            </ViewSourceFab>
          )}
        </ChatPanel>

        {/* PDF Viewer Panel */}
        <PDFViewerPanel $activeView={activeView}>
          {activePDF ? (
            <PDFViewer
              url={activePDF.url}
              initialPage={activePDF.page}
              showThumbnails={true}
              enableSearch={true}
              enableDownload={true}
              enableFullscreen={true}
              height="100%"
            />
          ) : (
            <PDFEmptyState>
              <PDFEmptyStateIcon>
                <Icon name="file-text" size="large" />
              </PDFEmptyStateIcon>
              <PDFEmptyStateText>
                Click a source to view the original document
              </PDFEmptyStateText>
            </PDFEmptyState>
          )}
        </PDFViewerPanel>
      </ModalContent>
    </ModalOverlay>,
    document.body
  )
}
